import React, { useState, useRef, useEffect } from 'react';
import {
  Plus, Trash2, Edit2, Eye, X, Send, ChevronRight, ChevronDown,
  Target, Megaphone, Smartphone, Mail, Bot, BarChart3, Calendar,
  CheckCircle, Clock, AlertCircle, ArrowRight, Sparkles, ThumbsUp,
  Instagram, Linkedin, Facebook, Globe, Search, Filter, Copy, RefreshCw
} from 'lucide-react';
import { toast } from '../utils/toast';
import { linkedinLogin, linkedinLogout, isLinkedinConnected, getLinkedinProfile, linkedinExchangeCode, linkedinPublishPost } from '../lib/linkedin';

// ═══════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════
const btnNovo = { display: 'flex', alignItems: 'center', gap: 6, background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };
const btnSecondary = { ...btnNovo, background: '#ebe7f3' };
const btnDanger = { ...btnNovo, background: '#da3633' };
const btnSmall = { ...btnNovo, padding: '4px 10px', fontSize: 12, borderRadius: 6 };
const card = { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 12, padding: '1.25rem' };
const inputSt = { background: '#f7f5fb', border: '1px solid #e5e0ef', borderRadius: 8, color: '#1c1530', padding: '8px 12px', fontSize: 13, width: '100%', outline: 'none', boxSizing: 'border-box' };
const labelSt = { fontSize: 11, color: '#5f5874', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 4 };
const selectSt = { ...inputSt, cursor: 'pointer' };
const textareaSt = { ...inputSt, minHeight: 80, resize: 'vertical', fontFamily: 'inherit' };
const badge = (cor) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: cor + '22', color: cor, border: `1px solid ${cor}44` });

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════
const LEAD_STAGES = ['Frio', 'Morno', 'Quente', 'Proposta', 'Cliente', 'Perdido'];
const LEAD_STAGE_COLORS = { Frio: '#3b82f6', Morno: '#f59e0b', Quente: '#ef4444', Proposta: '#a855f7', Cliente: '#10b981', Perdido: '#6b7280' };
const LEAD_SOURCES = ['Indicacao', 'Google', 'Instagram', 'LinkedIn', 'Email', 'Site', 'Outro'];
const CAMPAIGN_TYPES = ['social', 'email', 'google'];
const CAMPAIGN_STATUS = ['Rascunho', 'Pendente Aprovacao', 'Aprovada', 'Ativa', 'Concluida'];
const CAMPAIGN_STATUS_COLORS = { Rascunho: '#5f5874', 'Pendente Aprovacao': '#f59e0b', Aprovada: '#3b82f6', Ativa: '#10b981', Concluida: '#6b7280' };
const POST_STATUS = ['Rascunho', 'Pendente Aprovacao', 'Aprovado', 'Publicado'];
const POST_STATUS_COLORS = { Rascunho: '#5f5874', 'Pendente Aprovacao': '#f59e0b', Aprovado: '#3b82f6', Publicado: '#10b981' };
const NETWORKS = [
  { key: 'instagram', label: 'Instagram', icon: Instagram, color: '#E4405F' },
  { key: 'facebook', label: 'Facebook', icon: Facebook, color: '#1877F2' },
  { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: '#0A66C2' },
];

const TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { key: 'leads', label: 'Leads', icon: Target },
  { key: 'campanhas', label: 'Campanhas', icon: Megaphone },
  { key: 'social', label: 'Social Media', icon: Smartphone },
  { key: 'email', label: 'Email Marketing', icon: Mail },
  { key: 'ia', label: 'IA Assistente', icon: Bot },
];

const SYSTEM_PROMPT = `Voce e o Assistente de Marketing da Kzero Consultoria e Gestao, especialista em marketing digital B2B para consultorias de gestao da qualidade.

SOBRE A KZERO:
- 20+ anos no mercado de consultoria em SGQ
- Servicos: Consultoria (ISO 9001, 14001, 45001, IATF 16949), Auditoria, KzeroSystem (SaaS)
- Diferencial: sistemas praticos, sem burocracia desnecessaria
- Cases: Interforma, Sadel/Magna, Valge, Supercor
- Contato: (51) 99319.6154, comercial@kzeroconsultoria.com.br
- Instagram: @kzero_consultoria

PUBLICO-ALVO (ICP):
- Medias industrias metalmecanicas (nao restrito a setor)
- Empresas familiares
- Decisor: Gerente de Qualidade, 30-50 anos
- Dores: sistema burocratico, falta de ferramentas, exigencia de certificacao por grandes clientes

OBJETIVOS (6 meses):
1. Ser encontrado no Google para consultoria em gestao da qualidade
2. Gerar leads e orcamentos
3. Divulgar o KzeroSystem

TOM DE VOZ: Elegante, profissional, direto, sem jargoes excessivos. Transmitir autoridade e experiencia.

REGRAS:
- Sempre adapte o conteudo para cada rede social
- Instagram: visual, carrossel educativo, stories, hashtags relevantes
- LinkedIn: artigos tecnicos, cases de sucesso, autoridade
- Facebook: posts informativos, depoimentos, interacao
- Email: personalizado, valor agregado, CTA claro
- Nunca invente dados ou numeros, use os cases reais`;

// ═══════════════════════════════════════════════════════
// IA CALL
// ═══════════════════════════════════════════════════════
// Proxy server-side (/api/marketing-ia) — Gemini Flash 2.0 grátis, fallback
// pra Anthropic se ANTHROPIC_API_KEY estiver setada no Vercel. Sem chave
// vazando no bundle público (era a antiga REACT_APP_ANTHROPIC_KEY).
const callIA = async (prompt, systemPrompt = SYSTEM_PROMPT) => {
  try {
    const res = await fetch('/api/marketing-ia', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ prompt, systemPrompt, maxTokens: 2000 }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      toast.erro(data?.error || `HTTP ${res.status}`);
      return null;
    }
    return data.text || '';
  } catch (e) {
    toast.erro('Erro ao chamar IA: ' + e.message);
    return null;
  }
};

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════
export const Marketing = ({ marketing = {}, setMarketing, endomarketing = {}, setEndomarketing }) => {
  const [aba, setAba] = useState('dashboard');
  const [formLead, setFormLead] = useState(null);
  const [formCampanha, setFormCampanha] = useState(null);
  const [formPost, setFormPost] = useState(null);
  const [iaLoading, setIaLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef(null);
  const [linkedinOk, setLinkedinOk] = useState(isLinkedinConnected());

  // Processar callback LinkedIn OAuth
  useEffect(() => {
    const code = localStorage.getItem('linkedin_auth_code');
    const ts = parseInt(localStorage.getItem('linkedin_auth_ts') || '0');
    if (code && Date.now() - ts < 60000) {
      localStorage.removeItem('linkedin_auth_code');
      localStorage.removeItem('linkedin_auth_ts');
      linkedinExchangeCode(code)
        .then(() => { setLinkedinOk(true); toast.sucesso('LinkedIn conectado com sucesso!'); })
        .catch(err => toast.erro('Erro ao conectar LinkedIn: ' + err.message));
    }
  }, []);

  const leads = marketing.leads || [];
  const campanhas = marketing.campanhas || [];
  const socialPosts = marketing.socialPosts || [];
  const config = marketing.config || {};

  const set = (key, val) => setMarketing(prev => ({ ...(prev || {}), [key]: typeof val === 'function' ? val((prev || {})[key] || (key === 'config' ? {} : [])) : val }));
  const setLeads = (v) => set('leads', v);
  const setCampanhas = (v) => set('campanhas', v);
  const setSocialPosts = (v) => set('socialPosts', v);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  // ═══ LEAD FORM ═══
  const LEAD_VAZIO = () => ({ id: null, empresa: '', contato: '', email: '', telefone: '', cargo: '', fonte: 'Indicacao', stage: 'Frio', notas: '', criadoEm: new Date().toISOString().split('T')[0], qualificacao: null });

  const salvarLead = () => {
    if (!formLead.empresa) { toast.aviso('Informe a empresa.'); return; }
    if (formLead.id) {
      setLeads(prev => prev.map(l => l.id === formLead.id ? formLead : l));
      toast.sucesso('Lead atualizado!');
    } else {
      setLeads(prev => [...prev, { ...formLead, id: Date.now() }]);
      toast.sucesso('Lead adicionado!');
    }
    setFormLead(null);
  };

  const excluirLead = (id) => { setLeads(prev => prev.filter(l => l.id !== id)); toast.info('Lead removido.'); };
  const moverLead = (id, novoStage) => { setLeads(prev => prev.map(l => l.id === id ? { ...l, stage: novoStage } : l)); };

  const qualificarLead = async (lead) => {
    setIaLoading(true);
    const prompt = `Analise este lead e de uma pontuacao de qualificacao de 1 a 10, e sugira proximos passos:
Empresa: ${lead.empresa}
Contato: ${lead.contato} (${lead.cargo})
Fonte: ${lead.fonte}
Estagio: ${lead.stage}
Notas: ${lead.notas || 'Nenhuma'}
Responda em formato: SCORE: X/10 | ANALISE: ... | PROXIMO PASSO: ...`;
    const res = await callIA(prompt);
    setIaLoading(false);
    if (res) {
      setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, qualificacao: res } : l));
      toast.sucesso('Lead qualificado pela IA!');
    }
  };

  // ═══ CAMPANHA FORM ═══
  const CAMPANHA_VAZIA = () => ({ id: null, nome: '', tipo: 'social', status: 'Rascunho', dataInicio: '', dataFim: '', orcamento: '', publicoAlvo: '', objetivo: '', canais: [], conteudo: '', criadoEm: new Date().toISOString().split('T')[0] });

  const salvarCampanha = () => {
    if (!formCampanha.nome) { toast.aviso('Informe o nome da campanha.'); return; }
    if (formCampanha.id) {
      setCampanhas(prev => prev.map(c => c.id === formCampanha.id ? formCampanha : c));
      toast.sucesso('Campanha atualizada!');
    } else {
      setCampanhas(prev => [...prev, { ...formCampanha, id: Date.now() }]);
      toast.sucesso('Campanha criada!');
    }
    setFormCampanha(null);
  };

  const excluirCampanha = (id) => { setCampanhas(prev => prev.filter(c => c.id !== id)); toast.info('Campanha removida.'); };
  const avancarStatusCampanha = (id) => {
    setCampanhas(prev => prev.map(c => {
      if (c.id !== id) return c;
      const idx = CAMPAIGN_STATUS.indexOf(c.status);
      if (idx < CAMPAIGN_STATUS.length - 1) return { ...c, status: CAMPAIGN_STATUS[idx + 1] };
      return c;
    }));
    toast.sucesso('Status da campanha avancado!');
  };

  // ═══ POST FORM ═══
  const POST_VAZIO = () => ({ id: null, texto: '', hashtags: '', redes: ['instagram'], dataAgendada: '', horaAgendada: '', imagemUrl: '', status: 'Rascunho', criadoEm: new Date().toISOString().split('T')[0] });

  const salvarPost = () => {
    if (!formPost.texto) { toast.aviso('Informe o texto do post.'); return; }
    if (!formPost.redes?.length) { toast.aviso('Selecione pelo menos uma rede.'); return; }
    if (formPost.id) {
      setSocialPosts(prev => prev.map(p => p.id === formPost.id ? formPost : p));
      toast.sucesso('Post atualizado!');
    } else {
      setSocialPosts(prev => [...prev, { ...formPost, id: Date.now() }]);
      toast.sucesso('Post criado!');
    }
    setFormPost(null);
  };

  const excluirPost = (id) => { setSocialPosts(prev => prev.filter(p => p.id !== id)); toast.info('Post removido.'); };
  const aprovarPost = (id) => { setSocialPosts(prev => prev.map(p => p.id === id ? { ...p, status: 'Aprovado' } : p)); toast.sucesso('Post aprovado!'); };
  const publicarPost = async (id) => {
    const post = socialPosts.find(p => p.id === id);
    if (!post) return;
    const redes = post.redes || [];
    let publicouAlguma = false;
    let erros = [];

    // LinkedIn
    if (redes.includes('linkedin')) {
      if (isLinkedinConnected()) {
        try {
          await linkedinPublishPost(post.texto + (post.hashtags ? '\n\n' + post.hashtags : ''));
          publicouAlguma = true;
          toast.sucesso('Publicado no LinkedIn!');
        } catch (err) {
          erros.push('LinkedIn: ' + err.message);
        }
      } else {
        erros.push('LinkedIn: nao conectado. Clique em "Conectar LinkedIn" primeiro.');
      }
    }

    // Instagram / Facebook — API Meta pendente
    if (redes.includes('instagram') || redes.includes('facebook')) {
      erros.push('Instagram/Facebook: integracao Meta pendente (site developers.facebook.com estava fora). Copie e cole manualmente.');
    }

    setSocialPosts(prev => prev.map(p => p.id === id ? { ...p, status: 'Publicado', publicadoEm: new Date().toISOString() } : p));

    if (erros.length > 0) toast.aviso(erros.join(' | '));
    else if (publicouAlguma) toast.sucesso('Post publicado com sucesso!');
  };

  const gerarPostIA = async () => {
    setIaLoading(true);
    const redes = (formPost?.redes || ['instagram']).join(', ');
    const prompt = `Gere um post para as redes: ${redes}.
Tema: consultoria em gestao da qualidade / certificacao ISO.
Inclua: texto adaptado para cada rede, hashtags relevantes (separadas por espaco).
Formato de resposta:
TEXTO: [texto do post]
HASHTAGS: [hashtags separadas por espaco]`;
    const res = await callIA(prompt);
    setIaLoading(false);
    if (res) {
      const textoMatch = res.match(/TEXTO:\s*([\s\S]*?)(?=HASHTAGS:|$)/i);
      const hashMatch = res.match(/HASHTAGS:\s*(.*)/i);
      setFormPost(prev => ({
        ...prev,
        texto: textoMatch ? textoMatch[1].trim() : res,
        hashtags: hashMatch ? hashMatch[1].trim() : '',
      }));
      toast.sucesso('Post gerado pela IA!');
    }
  };

  // ═══ EMAIL IA ═══
  const gerarEmailIA = async (objetivo) => {
    setIaLoading(true);
    const prompt = `Crie um email marketing HTML profissional para a Kzero Consultoria.
Objetivo: ${objetivo || 'prospeccao de clientes para consultoria ISO 9001'}
Inclua: assunto, corpo HTML com design profissional (cores #0000FF e #6C3AED), CTA.
Formato: ASSUNTO: ... | CORPO: [HTML completo]`;
    const res = await callIA(prompt);
    setIaLoading(false);
    return res;
  };

  // ═══ CHAT IA ═══
  const enviarChat = async (texto) => {
    const msg = texto || chatInput;
    if (!msg.trim()) return;
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: msg }]);
    setIaLoading(true);
    const res = await callIA(msg);
    setIaLoading(false);
    if (res) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: res }]);
    }
  };

  const PROMPTS_RAPIDOS = [
    'Sugerir calendario editorial semanal',
    'Gerar copy para post sobre ISO 9001',
    'Criar email de prospeccao para industria metalmecanica',
    'Analisar meus leads e sugerir proximos passos',
    'Sugerir hashtags para Instagram',
  ];

  // ═══ METRICS ═══
  const totalLeads = leads.length;
  const leadsByStage = LEAD_STAGES.reduce((acc, s) => { acc[s] = leads.filter(l => l.stage === s).length; return acc; }, {});
  const campanhasAtivas = campanhas.filter(c => c.status === 'Ativa').length;
  const postsAgendados = socialPosts.filter(p => p.status !== 'Publicado').length;
  const emailsEnviados = (endomarketing?.historico || []).length;

  // ═══════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════
  return (
    <div style={{ color: '#1c1530' }}>
      {/* TABS */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, flexWrap: 'wrap', borderBottom: '1px solid #e5e0ef', paddingBottom: 8 }}>
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setAba(t.key)} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: '8px 8px 0 0',
              border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              background: aba === t.key ? '#7c3aed' : 'transparent', color: aba === t.key ? '#fff' : '#5f5874',
            }}>
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ═══ DASHBOARD ═══ */}
      {aba === 'dashboard' && (
        <div>
          {/* Metric Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Total Leads', value: totalLeads, color: '#3b82f6', icon: Target },
              { label: 'Campanhas Ativas', value: campanhasAtivas, color: '#10b981', icon: Megaphone },
              { label: 'Posts Agendados', value: postsAgendados, color: '#a855f7', icon: Smartphone },
              { label: 'Emails Enviados', value: emailsEnviados, color: '#f59e0b', icon: Mail },
            ].map((m, i) => {
              const Icon = m.icon;
              return (
                <div key={i} style={{ ...card, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ background: m.color + '22', borderRadius: 10, padding: 10, display: 'flex' }}>
                    <Icon size={20} color={m.color} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#5f5874', textTransform: 'uppercase' }}>{m.label}</div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{m.value}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Leads by Stage */}
          <div style={{ ...card, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Pipeline de Leads</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {LEAD_STAGES.map(s => (
                <div key={s} style={{ flex: 1, minWidth: 90, textAlign: 'center', padding: '10px 8px', background: LEAD_STAGE_COLORS[s] + '15', borderRadius: 8, border: `1px solid ${LEAD_STAGE_COLORS[s]}33` }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: LEAD_STAGE_COLORS[s] }}>{leadsByStage[s]}</div>
                  <div style={{ fontSize: 11, color: '#5f5874' }}>{s}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{ ...card, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Acoes Rapidas</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button style={btnNovo} onClick={() => { setAba('campanhas'); setFormCampanha(CAMPANHA_VAZIA()); }}>
                <Plus size={14} /> Nova Campanha
              </button>
              <button style={btnNovo} onClick={() => { setAba('leads'); setFormLead(LEAD_VAZIO()); }}>
                <Plus size={14} /> Novo Lead
              </button>
              <button style={btnNovo} onClick={() => { setAba('social'); setFormPost(POST_VAZIO()); }}>
                <Plus size={14} /> Novo Post
              </button>
            </div>
          </div>

          {/* Conexoes Redes Sociais */}
          <div style={{ ...card, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Conexoes de Redes Sociais</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {/* LinkedIn */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: '#0A66C222', border: '1px solid #0A66C244', borderRadius: 10 }}>
                <Linkedin size={20} color="#0A66C2" />
                {linkedinOk ? (
                  <>
                    <span style={{ color: '#10b981', fontSize: 13, fontWeight: 600 }}>Conectado</span>
                    <span style={{ color: '#5f5874', fontSize: 12 }}>{getLinkedinProfile()?.name || ''}</span>
                    <button onClick={() => { linkedinLogout(); setLinkedinOk(false); toast.info('LinkedIn desconectado.'); }}
                      style={{ ...btnSmall, background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', padding: '2px 8px' }}>
                      <X size={12} /> Desconectar
                    </button>
                  </>
                ) : (
                  <button onClick={() => linkedinLogin()} style={{ ...btnSmall, background: '#0A66C2' }}>
                    Conectar LinkedIn
                  </button>
                )}
              </div>
              {/* Instagram */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: '#E4405F22', border: '1px solid #E4405F44', borderRadius: 10 }}>
                <Instagram size={20} color="#E4405F" />
                <span style={{ color: '#f59e0b', fontSize: 13 }}>Meta API pendente</span>
              </div>
              {/* Facebook */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: '#1877F222', border: '1px solid #1877F244', borderRadius: 10 }}>
                <Facebook size={20} color="#1877F2" />
                <span style={{ color: '#f59e0b', fontSize: 13 }}>Meta API pendente</span>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div style={{ ...card }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Proximos Agendamentos</div>
            {(() => {
              const upcoming = [
                ...socialPosts.filter(p => p.dataAgendada && p.status !== 'Publicado').map(p => ({ tipo: 'Post', titulo: p.texto?.substring(0, 50) + '...', data: p.dataAgendada, cor: '#a855f7' })),
                ...campanhas.filter(c => c.dataInicio && c.status !== 'Concluida').map(c => ({ tipo: 'Campanha', titulo: c.nome, data: c.dataInicio, cor: '#10b981' })),
              ].sort((a, b) => a.data.localeCompare(b.data)).slice(0, 8);
              if (!upcoming.length) return <div style={{ color: '#5f5874', fontSize: 13 }}>Nenhum agendamento futuro.</div>;
              return upcoming.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < upcoming.length - 1 ? '1px solid #eeeaf5' : 'none' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.cor, flexShrink: 0 }} />
                  <div style={{ fontSize: 11, color: '#5f5874', minWidth: 80 }}>{item.data}</div>
                  <div style={badge(item.cor)}>{item.tipo}</div>
                  <div style={{ fontSize: 13, flex: 1 }}>{item.titulo}</div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* ═══ LEADS ═══ */}
      {aba === 'leads' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Pipeline de Leads ({totalLeads})</div>
            <button style={btnNovo} onClick={() => setFormLead(LEAD_VAZIO())}><Plus size={14} /> Novo Lead</button>
          </div>

          {/* Kanban */}
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${LEAD_STAGES.length}, minmax(170px, 1fr))`, gap: 10, overflowX: 'auto', paddingBottom: 8 }}>
            {LEAD_STAGES.map(stage => (
              <div key={stage} style={{ background: '#ffffff', borderRadius: 10, border: `1px solid ${LEAD_STAGE_COLORS[stage]}44`, minHeight: 200 }}>
                <div style={{ padding: '10px 12px', borderBottom: `2px solid ${LEAD_STAGE_COLORS[stage]}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: LEAD_STAGE_COLORS[stage] }}>{stage}</span>
                  <span style={{ fontSize: 11, color: '#5f5874' }}>{leadsByStage[stage]}</span>
                </div>
                <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {leads.filter(l => l.stage === stage).map(lead => (
                    <div key={lead.id} style={{ ...card, padding: '10px', cursor: 'pointer', fontSize: 12 }} onClick={() => setFormLead({ ...lead })}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{lead.empresa}</div>
                      <div style={{ color: '#5f5874' }}>{lead.contato}</div>
                      {lead.fonte && <div style={{ ...badge(LEAD_STAGE_COLORS[stage]), marginTop: 4, fontSize: 10 }}>{lead.fonte}</div>}
                      {lead.qualificacao && <div style={{ marginTop: 4, fontSize: 10, color: '#10b981' }}>IA: qualificado</div>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Lead Form Modal */}
          {formLead && (
            <div style={{ position: 'fixed', inset: 0, background: '#000000aa', zIndex: 999, display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => setFormLead(null)}>
              <div style={{ ...card, width: '95%', maxWidth: 550, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <span style={{ fontSize: 15, fontWeight: 600 }}>{formLead.id ? 'Editar Lead' : 'Novo Lead'}</span>
                  <X size={18} style={{ cursor: 'pointer', color: '#5f5874' }} onClick={() => setFormLead(null)} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div><label style={labelSt}>Empresa *</label><input style={inputSt} value={formLead.empresa} onChange={e => setFormLead(p => ({ ...p, empresa: e.target.value }))} /></div>
                  <div><label style={labelSt}>Contato</label><input style={inputSt} value={formLead.contato} onChange={e => setFormLead(p => ({ ...p, contato: e.target.value }))} /></div>
                  <div><label style={labelSt}>Email</label><input style={inputSt} value={formLead.email} onChange={e => setFormLead(p => ({ ...p, email: e.target.value }))} /></div>
                  <div><label style={labelSt}>Telefone</label><input style={inputSt} value={formLead.telefone} onChange={e => setFormLead(p => ({ ...p, telefone: e.target.value }))} /></div>
                  <div><label style={labelSt}>Cargo</label><input style={inputSt} value={formLead.cargo} onChange={e => setFormLead(p => ({ ...p, cargo: e.target.value }))} /></div>
                  <div><label style={labelSt}>Fonte</label>
                    <select style={selectSt} value={formLead.fonte} onChange={e => setFormLead(p => ({ ...p, fonte: e.target.value }))}>
                      {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div><label style={labelSt}>Estagio</label>
                    <select style={selectSt} value={formLead.stage} onChange={e => setFormLead(p => ({ ...p, stage: e.target.value }))}>
                      {LEAD_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div><label style={labelSt}>Data</label><input style={inputSt} type="date" value={formLead.criadoEm} onChange={e => setFormLead(p => ({ ...p, criadoEm: e.target.value }))} /></div>
                </div>
                <div style={{ marginTop: 10 }}><label style={labelSt}>Notas</label><textarea style={textareaSt} value={formLead.notas} onChange={e => setFormLead(p => ({ ...p, notas: e.target.value }))} /></div>

                {formLead.qualificacao && (
                  <div style={{ marginTop: 10, padding: 10, background: '#10b98115', borderRadius: 8, border: '1px solid #10b98133', fontSize: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4, color: '#10b981' }}>Qualificacao IA</div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{formLead.qualificacao}</div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  {formLead.id && (
                    <>
                      <button style={{ ...btnSmall, background: '#6C3AED' }} onClick={() => qualificarLead(formLead)} disabled={iaLoading}>
                        <Sparkles size={13} /> {iaLoading ? 'Analisando...' : 'Qualificar Lead'}
                      </button>
                      <button style={{ ...btnSmall, ...btnDanger }} onClick={() => { excluirLead(formLead.id); setFormLead(null); }}>
                        <Trash2 size={13} /> Excluir
                      </button>
                    </>
                  )}
                  <button style={btnSecondary} onClick={() => setFormLead(null)}>Cancelar</button>
                  <button style={btnNovo} onClick={salvarLead}>Salvar</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ CAMPANHAS ═══ */}
      {aba === 'campanhas' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Campanhas ({campanhas.length})</div>
            <button style={btnNovo} onClick={() => setFormCampanha(CAMPANHA_VAZIA())}><Plus size={14} /> Nova Campanha</button>
          </div>

          {!campanhas.length && <div style={{ ...card, textAlign: 'center', color: '#5f5874' }}>Nenhuma campanha criada.</div>}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {campanhas.map(c => (
              <div key={c.id} style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{c.nome}</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={badge(CAMPAIGN_STATUS_COLORS[c.status] || '#5f5874')}>{c.status}</span>
                    <span style={{ fontSize: 11, color: '#5f5874' }}>{c.tipo}</span>
                    {c.dataInicio && <span style={{ fontSize: 11, color: '#5f5874' }}>{c.dataInicio} - {c.dataFim || '?'}</span>}
                    {c.orcamento && <span style={{ fontSize: 11, color: '#f59e0b' }}>R$ {c.orcamento}</span>}
                  </div>
                  {c.objetivo && <div style={{ fontSize: 12, color: '#5f5874', marginTop: 4 }}>{c.objetivo}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {c.status !== 'Concluida' && (
                    <button style={btnSmall} onClick={() => avancarStatusCampanha(c.id)} title="Avancar status">
                      <ArrowRight size={13} /> Avancar
                    </button>
                  )}
                  <button style={{ ...btnSmall, background: '#ebe7f3' }} onClick={() => setFormCampanha({ ...c })}>
                    <Edit2 size={13} />
                  </button>
                  <button style={{ ...btnSmall, background: '#da3633' }} onClick={() => excluirCampanha(c.id)}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Campanha Form Modal */}
          {formCampanha && (
            <div style={{ position: 'fixed', inset: 0, background: '#000000aa', zIndex: 999, display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => setFormCampanha(null)}>
              <div style={{ ...card, width: '95%', maxWidth: 550, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <span style={{ fontSize: 15, fontWeight: 600 }}>{formCampanha.id ? 'Editar Campanha' : 'Nova Campanha'}</span>
                  <X size={18} style={{ cursor: 'pointer', color: '#5f5874' }} onClick={() => setFormCampanha(null)} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ gridColumn: '1 / -1' }}><label style={labelSt}>Nome *</label><input style={inputSt} value={formCampanha.nome} onChange={e => setFormCampanha(p => ({ ...p, nome: e.target.value }))} /></div>
                  <div><label style={labelSt}>Tipo</label>
                    <select style={selectSt} value={formCampanha.tipo} onChange={e => setFormCampanha(p => ({ ...p, tipo: e.target.value }))}>
                      {CAMPAIGN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div><label style={labelSt}>Status</label>
                    <select style={selectSt} value={formCampanha.status} onChange={e => setFormCampanha(p => ({ ...p, status: e.target.value }))}>
                      {CAMPAIGN_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div><label style={labelSt}>Data Inicio</label><input style={inputSt} type="date" value={formCampanha.dataInicio} onChange={e => setFormCampanha(p => ({ ...p, dataInicio: e.target.value }))} /></div>
                  <div><label style={labelSt}>Data Fim</label><input style={inputSt} type="date" value={formCampanha.dataFim} onChange={e => setFormCampanha(p => ({ ...p, dataFim: e.target.value }))} /></div>
                  <div><label style={labelSt}>Orcamento (R$)</label><input style={inputSt} value={formCampanha.orcamento} onChange={e => setFormCampanha(p => ({ ...p, orcamento: e.target.value }))} /></div>
                  <div><label style={labelSt}>Publico Alvo</label><input style={inputSt} value={formCampanha.publicoAlvo} onChange={e => setFormCampanha(p => ({ ...p, publicoAlvo: e.target.value }))} /></div>
                </div>
                <div style={{ marginTop: 10 }}><label style={labelSt}>Objetivo</label><textarea style={textareaSt} value={formCampanha.objetivo} onChange={e => setFormCampanha(p => ({ ...p, objetivo: e.target.value }))} /></div>
                <div style={{ marginTop: 10 }}><label style={labelSt}>Conteudo / Pecas</label><textarea style={{ ...textareaSt, minHeight: 60 }} value={formCampanha.conteudo} onChange={e => setFormCampanha(p => ({ ...p, conteudo: e.target.value }))} /></div>
                <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                  <button style={btnSecondary} onClick={() => setFormCampanha(null)}>Cancelar</button>
                  <button style={btnNovo} onClick={salvarCampanha}>Salvar</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ SOCIAL MEDIA ═══ */}
      {aba === 'social' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Social Media ({socialPosts.length})</div>
            <button style={btnNovo} onClick={() => setFormPost(POST_VAZIO())}><Plus size={14} /> Novo Post</button>
          </div>

          {!socialPosts.length && <div style={{ ...card, textAlign: 'center', color: '#5f5874' }}>Nenhum post criado.</div>}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
            {socialPosts.map(p => (
              <div key={p.id} style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {(p.redes || []).map(r => {
                      const net = NETWORKS.find(n => n.key === r);
                      if (!net) return null;
                      const NetIcon = net.icon;
                      return <NetIcon key={r} size={16} color={net.color} />;
                    })}
                  </div>
                  <span style={badge(POST_STATUS_COLORS[p.status] || '#5f5874')}>{p.status}</span>
                </div>
                <div style={{ fontSize: 13, marginBottom: 8, whiteSpace: 'pre-wrap', maxHeight: 100, overflow: 'hidden' }}>{p.texto}</div>
                {p.hashtags && <div style={{ fontSize: 11, color: '#6C3AED', marginBottom: 6 }}>{p.hashtags}</div>}
                {p.dataAgendada && <div style={{ fontSize: 11, color: '#5f5874' }}>Agendado: {p.dataAgendada} {p.horaAgendada || ''}</div>}
                <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                  <button style={{ ...btnSmall, background: '#ebe7f3' }} onClick={() => setFormPost({ ...p })}><Edit2 size={12} /> Editar</button>
                  {p.status === 'Rascunho' && (
                    <button style={{ ...btnSmall, background: '#f59e0b' }} onClick={() => setSocialPosts(prev => prev.map(x => x.id === p.id ? { ...x, status: 'Pendente Aprovacao' } : x))}>
                      Enviar p/ Aprovacao
                    </button>
                  )}
                  {p.status === 'Pendente Aprovacao' && (
                    <button style={{ ...btnSmall, background: '#10b981' }} onClick={() => aprovarPost(p.id)}>
                      <ThumbsUp size={12} /> Aprovar
                    </button>
                  )}
                  {p.status === 'Aprovado' && (
                    <button style={{ ...btnSmall, background: '#3b82f6' }} onClick={() => publicarPost(p.id)}>
                      <Send size={12} /> Publicar
                    </button>
                  )}
                  <button style={{ ...btnSmall, background: '#da3633' }} onClick={() => excluirPost(p.id)}><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
          </div>

          {/* Post Form Modal */}
          {formPost && (
            <div style={{ position: 'fixed', inset: 0, background: '#000000aa', zIndex: 999, display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => setFormPost(null)}>
              <div style={{ ...card, width: '95%', maxWidth: 650, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <span style={{ fontSize: 15, fontWeight: 600 }}>{formPost.id ? 'Editar Post' : 'Novo Post'}</span>
                  <X size={18} style={{ cursor: 'pointer', color: '#5f5874' }} onClick={() => setFormPost(null)} />
                </div>

                {/* Network Selection */}
                <div style={{ marginBottom: 12 }}>
                  <label style={labelSt}>Redes Sociais *</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {NETWORKS.map(net => {
                      const NetIcon = net.icon;
                      const selected = (formPost.redes || []).includes(net.key);
                      return (
                        <button key={net.key} style={{
                          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, border: `1px solid ${selected ? net.color : '#e5e0ef'}`,
                          background: selected ? net.color + '22' : 'transparent', color: selected ? net.color : '#5f5874', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        }} onClick={() => {
                          setFormPost(prev => ({
                            ...prev,
                            redes: selected ? prev.redes.filter(r => r !== net.key) : [...(prev.redes || []), net.key]
                          }));
                        }}>
                          <NetIcon size={14} /> {net.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div><label style={labelSt}>Texto do Post *</label><textarea style={{ ...textareaSt, minHeight: 120 }} value={formPost.texto} onChange={e => setFormPost(p => ({ ...p, texto: e.target.value }))} /></div>
                <div style={{ marginTop: 10 }}><label style={labelSt}>Hashtags</label><input style={inputSt} value={formPost.hashtags} onChange={e => setFormPost(p => ({ ...p, hashtags: e.target.value }))} placeholder="#qualidade #iso9001 #gestao" /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                  <div><label style={labelSt}>Data Agendada</label><input style={inputSt} type="date" value={formPost.dataAgendada} onChange={e => setFormPost(p => ({ ...p, dataAgendada: e.target.value }))} /></div>
                  <div><label style={labelSt}>Hora</label><input style={inputSt} type="time" value={formPost.horaAgendada} onChange={e => setFormPost(p => ({ ...p, horaAgendada: e.target.value }))} /></div>
                </div>
                <div style={{ marginTop: 10 }}><label style={labelSt}>URL da Imagem (opcional)</label><input style={inputSt} value={formPost.imagemUrl} onChange={e => setFormPost(p => ({ ...p, imagemUrl: e.target.value }))} /></div>

                {/* Preview Cards */}
                {formPost.texto && (
                  <div style={{ marginTop: 16 }}>
                    <label style={labelSt}>Preview</label>
                    <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8 }}>
                      {(formPost.redes || []).map(r => {
                        const net = NETWORKS.find(n => n.key === r);
                        if (!net) return null;
                        const NetIcon = net.icon;
                        return (
                          <div key={r} style={{ minWidth: 240, maxWidth: 280, background: '#ffffff', borderRadius: 10, border: `1px solid ${net.color}33`, overflow: 'hidden', flexShrink: 0 }}>
                            <div style={{ padding: '8px 12px', borderBottom: `1px solid ${net.color}33`, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <NetIcon size={14} color={net.color} />
                              <span style={{ fontSize: 12, fontWeight: 600, color: net.color }}>{net.label}</span>
                            </div>
                            <div style={{ padding: 12 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#6C3AED', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff' }}>K</div>
                                <div>
                                  <div style={{ fontSize: 12, fontWeight: 600 }}>Kzero Consultoria</div>
                                  <div style={{ fontSize: 10, color: '#5f5874' }}>@kzero_consultoria</div>
                                </div>
                              </div>
                              {formPost.imagemUrl && <div style={{ width: '100%', height: 120, background: '#f7f5fb', borderRadius: 6, marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src={formPost.imagemUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 6 }} onError={e => { e.target.style.display = 'none'; }} /></div>}
                              <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 80, overflow: 'hidden' }}>
                                {r === 'linkedin' ? formPost.texto.substring(0, 200) : formPost.texto.substring(0, 150)}
                                {formPost.texto.length > 150 && '...'}
                              </div>
                              {formPost.hashtags && <div style={{ fontSize: 10, color: '#6C3AED', marginTop: 4 }}>{formPost.hashtags}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button style={{ ...btnSmall, background: '#6C3AED' }} onClick={gerarPostIA} disabled={iaLoading}>
                    <Sparkles size={13} /> {iaLoading ? 'Gerando...' : 'Gerar Post com IA'}
                  </button>
                  <button style={btnSecondary} onClick={() => setFormPost(null)}>Cancelar</button>
                  <button style={btnNovo} onClick={salvarPost}>Salvar</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ EMAIL MARKETING ═══ */}
      {aba === 'email' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Email Marketing</div>
            <button style={{ ...btnSmall, background: '#6C3AED' }} onClick={async () => {
              const objetivo = prompt('Qual o objetivo do email? (ex: prospeccao ISO 9001)');
              if (!objetivo) return;
              const res = await gerarEmailIA(objetivo);
              if (res) {
                const el = document.createElement('textarea');
                el.value = res;
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
                toast.sucesso('Template de email copiado para a area de transferencia!');
              }
            }} disabled={iaLoading}>
              <Sparkles size={13} /> {iaLoading ? 'Gerando...' : 'Gerar Email com IA'}
            </button>
          </div>

          {/* Render Endomarketing inline */}
          {setEndomarketing ? (
            <div style={{ ...card }}>
              <div style={{ fontSize: 13, color: '#5f5874', marginBottom: 12 }}>
                Use o modulo de Endomarketing abaixo para gerenciar listas de contatos, campanhas de email e envios.
              </div>
              {/* Import Endomarketing dynamically */}
              <EndomarketingInline endomarketing={endomarketing} setEndomarketing={setEndomarketing} />
            </div>
          ) : (
            <div style={{ ...card, textAlign: 'center', color: '#5f5874' }}>
              Modulo de Endomarketing nao disponivel. Configure as props endomarketing/setEndomarketing.
            </div>
          )}
        </div>
      )}

      {/* ═══ IA ASSISTENTE ═══ */}
      {aba === 'ia' && (
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Assistente de Marketing IA</div>

          {/* Quick Prompts */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
            {PROMPTS_RAPIDOS.map((p, i) => (
              <button key={i} style={{ ...btnSmall, background: '#f7f5fb', border: '1px solid #e5e0ef' }} onClick={() => enviarChat(p)}>
                {p}
              </button>
            ))}
          </div>

          {/* Chat */}
          <div style={{ ...card, height: 400, display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12, paddingRight: 4 }}>
              {!chatMessages.length && (
                <div style={{ textAlign: 'center', color: '#5f5874', marginTop: 60, fontSize: 13 }}>
                  Pergunte sobre estrategia de marketing, geracao de conteudo, leads, SEO, redes sociais...
                </div>
              )}
              {chatMessages.map((msg, i) => (
                <div key={i} style={{
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '80%', padding: '10px 14px', borderRadius: 12, fontSize: 13,
                  background: msg.role === 'user' ? '#7c3aed' : '#f7f5fb',
                  border: msg.role === 'user' ? 'none' : '1px solid #e5e0ef',
                  whiteSpace: 'pre-wrap',
                }}>
                  {msg.content}
                </div>
              ))}
              {iaLoading && (
                <div style={{ alignSelf: 'flex-start', padding: '10px 14px', borderRadius: 12, background: '#f7f5fb', border: '1px solid #e5e0ef', fontSize: 13, color: '#5f5874' }}>
                  Pensando...
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                style={{ ...inputSt, flex: 1 }}
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && enviarChat()}
                placeholder="Digite sua pergunta..."
              />
              <button style={btnNovo} onClick={() => enviarChat()} disabled={iaLoading || !chatInput.trim()}>
                <Send size={14} />
              </button>
            </div>
          </div>

          {/* Context Info */}
          <div style={{ ...card, marginTop: 12, fontSize: 12, color: '#5f5874' }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Contexto carregado:</div>
            <div>Kzero Consultoria | ICP: Gerentes de Qualidade, 30-50 anos, industrias metalmecanicas | Cases: Interforma, Sadel/Magna, Valge, Supercor | Tom: profissional e elegante</div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════
// Endomarketing Inline Wrapper (lazy import)
// ═══════════════════════════════════════════════════════
const EndomarketingInline = ({ endomarketing, setEndomarketing }) => {
  const [Comp, setComp] = useState(null);
  useEffect(() => {
    import('./Endomarketing').then(mod => {
      setComp(() => mod.Endomarketing);
    }).catch(() => {
      // fallback: render nothing
    });
  }, []);
  if (!Comp) return <div style={{ color: '#5f5874', fontSize: 13, padding: 20, textAlign: 'center' }}>Carregando modulo de Email...</div>;
  return <Comp endomarketing={endomarketing} setEndomarketing={setEndomarketing} />;
};
